import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { useDropzone, Accept } from "react-dropzone"; // ✅ Accept imported here
import { UploadCloud } from "lucide-react";
import { Typography } from "../../general/Typography";
import { Flex } from "../../layouts/wrappers/Flex";

interface UploadDropZoneProps {
  onDrop: (files: File[]) => void;
  accept?: Accept; // ✅ Use the correct type here
  multiple?: boolean;
  disabled?: boolean;
}

const DropWrapper = styled.div<{ isDragActive: boolean; isDisabled: boolean }>`
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s ease, background 0.2s ease;
  color: ${({ theme }) => theme.colors.textSecondary};

  ${({ isDragActive, theme }) =>
    isDragActive &&
    css`
      border-color: ${theme.colors.primary};
      background: ${theme.colors.surfaceHover};
    `}

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const IconContainer = styled(Flex)`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const UploadDropZone: React.FC<UploadDropZoneProps> = ({
  onDrop,
  accept = { "application/pdf": [], "video/mp4": [], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [] },
  multiple = true,
  disabled = false,
}) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;
      onDrop(acceptedFiles);
    },
    [onDrop, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept,
    multiple,
    disabled,
  });

  return (
    <DropWrapper {...getRootProps()} isDragActive={isDragActive} isDisabled={disabled}>
      <input {...getInputProps()} />
      <IconContainer align="center" justify="center">
        <UploadCloud size={32} />
      </IconContainer>
      <Typography as="span" variant="caption" colorScheme="textSecondary">
        {isDragActive ? "Drop files here..." : "Drag & drop or click to upload course material"}
      </Typography>
    </DropWrapper>
  );
};